import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Check,
  Utensils,
  Dumbbell,
  Brain,
  Target,
  Home as HomeIcon,
  TrendingUp,
  Activity as ActivityIcon,
  User,
  Droplets,
  Clock,
  Flame,
  Sparkles
} from 'lucide-react';
import { supabase } from '../lib/supabase';

type ActivePage = 'home' | 'progress' | 'activity' | 'profile';

interface TodaysRoutineProps {
  onBack: () => void;
  onNavigate: (page: ActivePage) => void;
  selectedDate: Date;
}

interface MealItem {
  id: string;
  text: string;
  completed: boolean;
}

interface ExerciseItem {
  id: string;
  text: string;
  duration?: number;
  completed: boolean;
}

interface CustomHabit {
  id: string;
  habit_name: string;
  is_completed: boolean;
}

interface RoutineData {
  id: string;
  diet_plan: {
    meals: MealItem[];
    target_calories: number;
    water_glasses: number;
    water_completed: number;
    notes: string;
  };
  physical_exercise: {
    exercises: ExerciseItem[];
    total_duration: number;
    notes: string;
  };
  mental_exercise: {
    activities: ExerciseItem[];
    total_duration: number;
    notes: string;
  };
}

const generateId = () => Math.random().toString(36).substr(2, 9);

export default function TodaysRoutine({ onBack, onNavigate, selectedDate }: TodaysRoutineProps) {
  const [routine, setRoutine] = useState<RoutineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [customHabits, setCustomHabits] = useState<CustomHabit[]>([]);

  useEffect(() => {
    loadRoutine();
    loadCustomHabits();
  }, [selectedDate]);

  const formatDateForDB = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadCustomHabits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = formatDateForDB(selectedDate);
      
     const { data: habits, error } = await supabase  // ADD error here
      .from('custom_habits')
      .select('id, habit_name, start_date, end_date')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .lte('start_date', dateStr)
      .gte('end_date', dateStr);
      
      if (habits) {
        const habitsWithCompletion = await Promise.all(
          habits.map(async (habit) => {
            const { data: completion } = await supabase
              .from('custom_habit_completions')
              .select('id')
              .eq('habit_id', habit.id)
              .eq('date', dateStr)
              .maybeSingle();

            return {
              id: habit.id,
              habit_name: habit.habit_name,
              is_completed: !!completion,
            };
          })
        );

        setCustomHabits(habitsWithCompletion);
      }
    } catch (error) {
      console.error('Error loading custom habits:', error);
    }
  };

  const loadRoutine = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = formatDateForDB(selectedDate);

      let { data: existingRoutine } = await supabase
        .from('daily_routine_templates')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', dateStr)
        .maybeSingle();

      if (existingRoutine) {
        const { data: protocol } = await supabase
          .from('user_protocols')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (protocol && protocol.protocol_generated && protocol.protocol_data) {
          const needsMigration =
            !existingRoutine.diet_plan?.meals ||
            existingRoutine.diet_plan.meals.length === 0 ||
            typeof existingRoutine.diet_plan.meals[0] === 'string' ||
            !existingRoutine.diet_plan.meals[0]?.id;

          const protocolGenerationDate = protocol.generation_date ? new Date(protocol.generation_date) : null;
          const routineCreationDate = existingRoutine.created_at ? new Date(existingRoutine.created_at) : null;

          const isProtocolNewer = protocolGenerationDate && routineCreationDate &&
            protocolGenerationDate > routineCreationDate;

          if (needsMigration || isProtocolNewer) {
            const routineTemplate = createRoutineFromProtocol(protocol);

            const waterCompleted = existingRoutine.diet_plan?.water_completed || 0;
            routineTemplate.diet_plan.water_completed = waterCompleted;

            const { error } = await supabase
              .from('daily_routine_templates')
              .update(routineTemplate)
              .eq('id', existingRoutine.id);

            if (error) throw error;

            existingRoutine = { ...existingRoutine, ...routineTemplate };
          }
        }
        setRoutine(existingRoutine);
      } else {
        const { data: protocol } = await supabase
          .from('user_protocols')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (protocol && protocol.protocol_generated && protocol.protocol_data) {
          const routineTemplate = createRoutineFromProtocol(protocol);

          // Use upsert instead of insert to handle race conditions
          const { data: newRoutine, error } = await supabase
            .from('daily_routine_templates')
            .upsert({
              user_id: user.id,
              date: dateStr,
              ...routineTemplate
            }, {
              onConflict: 'user_id,date',
              ignoreDuplicates: false
            })
            .select()
            .single();

          if (error) {
            console.error('Error upserting routine:', error);
            // If upsert fails, try to fetch the existing routine
            const { data: fallbackRoutine } = await supabase
              .from('daily_routine_templates')
              .select('*')
              .eq('user_id', user.id)
              .eq('date', dateStr)
              .single();
            
            if (fallbackRoutine) {
              setRoutine(fallbackRoutine);
              return;
            }
            throw error;
          }
          setRoutine(newRoutine);
        } else {
          const emptyRoutine = {
            diet_plan: {
              meals: [],
              target_calories: 2500,
              water_glasses: 8,
              water_completed: 0,
              notes: 'Generate a protocol to get personalized meal suggestions!'
            },
            physical_exercise: {
              exercises: [],
              total_duration: 0,
              notes: 'Generate a protocol to get personalized exercise plans!'
            },
            mental_exercise: {
              activities: [],
              total_duration: 0,
              notes: 'Generate a protocol to get personalized mental wellness activities!'
            }
          };

          // Use upsert for empty routine as well
          const { data: newRoutine, error } = await supabase
            .from('daily_routine_templates')
            .upsert({
              user_id: user.id,
              date: dateStr,
              ...emptyRoutine
            }, {
              onConflict: 'user_id,date',
              ignoreDuplicates: false
            })
            .select()
            .single();

          if (error) {
            console.error('Error upserting empty routine:', error);
            // If upsert fails, try to fetch the existing routine
            const { data: fallbackRoutine } = await supabase
              .from('daily_routine_templates')
              .select('*')
              .eq('user_id', user.id)
              .eq('date', dateStr)
              .single();
            
            if (fallbackRoutine) {
              setRoutine(fallbackRoutine);
              return;
            }
            throw error;
          }
          setRoutine(newRoutine);
        }
      }
    } catch (error) {
      console.error('Error loading routine:', error);
    } finally {
      setLoading(false);
    }
  };

  const createRoutineFromProtocol = (protocol: any) => {
    const protocolData = protocol.protocol_data;
    const currentDay = protocol.current_day || 1;

    const mentalActivities = (protocolData.mental_exercises?.selected_activities || []).map((activity: string) => ({
      id: generateId(),
      text: activity,
      duration: Math.floor((protocolData.mental_exercises?.daily_duration_minutes || 20) / Math.max((protocolData.mental_exercises?.selected_activities?.length || 1), 1)),
      completed: false
    }));

    const dietPlan = {
      meals: (protocolData.diet_plan?.meal_suggestions || []).map((meal: string) => ({
        id: generateId(),
        text: meal,
        completed: false
      })),
      target_calories: protocolData.diet_plan?.daily_calories || 2500,
      water_glasses: protocolData.diet_plan?.water_glasses || 8,
      water_completed: 0,
      notes: protocolData.diet_plan?.notes || 'Stay hydrated throughout the day!'
    };

    let exercisePlan = protocolData.exercise_plan?.phase1 || {};
    if (currentDay >= 8 && currentDay <= 14) {
      exercisePlan = protocolData.exercise_plan?.phase2 || exercisePlan;
    } else if (currentDay >= 15 && currentDay <= 21) {
      exercisePlan = protocolData.exercise_plan?.phase3 || exercisePlan;
    }

    const physicalExercises = (exercisePlan.activities || []).map((activity: string) => ({
      id: generateId(),
      text: activity,
      duration: Math.floor((exercisePlan.duration_minutes || 30) / Math.max((exercisePlan.activities?.length || 1), 1)),
      completed: false
    }));

    return {
      diet_plan: dietPlan,
      physical_exercise: {
        exercises: physicalExercises,
        total_duration: exercisePlan.duration_minutes || 30,
        notes: protocolData.exercise_plan?.notes || 'Warm up before and cool down after exercise.'
      },
      mental_exercise: {
        activities: mentalActivities,
        total_duration: protocolData.mental_exercises?.daily_duration_minutes || 20,
        notes: protocolData.mental_exercises?.notes || 'Find a quiet space and focus on your breath.'
      }
    };
  };

  const updateRoutine = async (updates: Partial<RoutineData>) => {
    if (!routine) return;

    try {
      const { error } = await supabase
        .from('daily_routine_templates')
        .update(updates)
        .eq('id', routine.id);

      if (error) throw error;

      setRoutine({ ...routine, ...updates });
    } catch (error) {
      console.error('Error updating routine:', error);
    }
  };

  const toggleMeal = (mealId: string) => {
    if (!routine) return;
    const updatedMeals = routine.diet_plan.meals.map(meal =>
      meal.id === mealId ? { ...meal, completed: !meal.completed } : meal
    );
    updateRoutine({
      diet_plan: { ...routine.diet_plan, meals: updatedMeals }
    });
  };

  const toggleExercise = (exerciseId: string) => {
    if (!routine) return;
    const updatedExercises = routine.physical_exercise.exercises.map(ex =>
      ex.id === exerciseId ? { ...ex, completed: !ex.completed } : ex
    );
    updateRoutine({
      physical_exercise: { ...routine.physical_exercise, exercises: updatedExercises }
    });
  };

  const toggleMentalActivity = (activityId: string) => {
    if (!routine) return;
    const updatedActivities = routine.mental_exercise.activities.map(act =>
      act.id === activityId ? { ...act, completed: !act.completed } : act
    );
    updateRoutine({
      mental_exercise: { ...routine.mental_exercise, activities: updatedActivities }
    });
  };

  const addWater = () => {
    if (!routine) return;
    const newCount = Math.min(routine.diet_plan.water_completed + 1, routine.diet_plan.water_glasses);
    updateRoutine({
      diet_plan: { ...routine.diet_plan, water_completed: newCount }
    });
  };

  const toggleCustomHabit = async (habitId: string, isCompleted: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = formatDateForDB(selectedDate);

      if (isCompleted) {
        await supabase
          .from('custom_habit_completions')
          .delete()
          .eq('habit_id', habitId)
          .eq('date', dateStr);
      } else {
        await supabase
          .from('custom_habit_completions')
          .insert({
            habit_id: habitId,
            user_id: user.id,
            date: dateStr,
          });
      }

      loadCustomHabits();
    } catch (error) {
      console.error('Error toggling custom habit:', error);
    }
  };

  const getTotalCompletion = () => {
    if (!routine) return 0;

    const mealsDone = routine.diet_plan.meals.filter(m => m.completed).length;
    const mealsTotal = routine.diet_plan.meals.length;

    const exercisesDone = routine.physical_exercise.exercises.filter(e => e.completed).length;
    const exercisesTotal = routine.physical_exercise.exercises.length;

    const mentalDone = routine.mental_exercise.activities.filter(a => a.completed).length;
    const mentalTotal = routine.mental_exercise.activities.length;

    const waterDone = routine.diet_plan.water_completed;
    const waterTotal = routine.diet_plan.water_glasses;

    const habitsDone = customHabits.filter(h => h.is_completed).length;
    const habitsTotal = customHabits.length;

    const total = mealsTotal + exercisesTotal + mentalTotal + waterTotal + habitsTotal;
    const done = mealsDone + exercisesDone + mentalDone + waterDone + habitsDone;

    return total > 0 ? Math.round((done / total) * 100) : 0;
  };

  const getCompletedDuration = () => {
    if (!routine) return 0;
    return routine.physical_exercise.exercises
      .filter(e => e.completed)
      .reduce((sum, e) => sum + (e.duration || 0), 0);
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <header className="bg-white px-6 py-4 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-700" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {isToday ? "Today's Routine" : 'Daily Routine'}
              </h1>
              <p className="text-xs text-gray-500">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-2xl font-black text-cyan-600">{getTotalCompletion()}%</div>
              <p className="text-xs text-gray-500">Complete</p>
            </div>
          </div>
        </header>

        <div className="p-4 space-y-4">
          {routine && (
            <>
              {routine.diet_plan.meals.length === 0 &&
               routine.physical_exercise.exercises.length === 0 &&
               routine.mental_exercise.activities.length === 0 && (
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border-2 border-cyan-200 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-100 flex items-center justify-center">
                    <Target className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    No Routine Yet
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Generate a personalized protocol to get started with your daily routine
                  </p>
                  <button
                    onClick={() => onNavigate('profile')}
                    className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition-colors"
                  >
                    Generate Protocol
                  </button>
                </div>
              )}

              {/* DIET SECTION - Card with meal list + water tracker */}
              {routine.diet_plan.meals.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                      <Utensils className="w-5 h-5 text-green-600" strokeWidth={2} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-bold text-gray-900">Diet Plan</h2>
                      <p className="text-xs text-gray-500">{routine.diet_plan.target_calories} cal target</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-green-600">
                        {routine.diet_plan.meals.filter(m => m.completed).length}/{routine.diet_plan.meals.length}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 space-y-2">
                  {routine.diet_plan.meals.map((meal) => (
                    <button
                      key={meal.id}
                      onClick={() => toggleMeal(meal.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                        meal.completed 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-gray-50 border border-transparent hover:border-gray-200'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        meal.completed 
                          ? 'bg-green-500 border-green-500' 
                          : 'border-gray-300'
                      }`}>
                        {meal.completed && <Check size={14} className="text-white" strokeWidth={3} />}
                      </div>
                      <span className={`text-sm text-left flex-1 ${
                        meal.completed ? 'text-green-700 line-through' : 'text-gray-700'
                      }`}>
                        {meal.text}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Water Tracker */}
                <div className="px-4 pb-4">
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Droplets className="w-5 h-5 text-blue-500" />
                        <span className="font-semibold text-gray-900 text-sm">Water Intake</span>
                      </div>
                      <span className="text-sm font-bold text-blue-600">
                        {routine.diet_plan.water_completed}/{routine.diet_plan.water_glasses} glasses
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {Array.from({ length: routine.diet_plan.water_glasses }).map((_, i) => (
                        <button
                          key={i}
                          onClick={addWater}
                          disabled={i < routine.diet_plan.water_completed}
                          className={`flex-1 h-8 rounded-lg transition-all ${
                            i < routine.diet_plan.water_completed
                              ? 'bg-blue-500'
                              : 'bg-blue-200 hover:bg-blue-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* PHYSICAL EXERCISE - Horizontal scroll cards */}
              {routine.physical_exercise.exercises.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                      <Dumbbell className="w-5 h-5 text-orange-600" strokeWidth={2} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-bold text-gray-900">Physical Exercise</h2>
                      <p className="text-xs text-gray-500">{routine.physical_exercise.total_duration} min total</p>
                    </div>
                    <div className="flex items-center gap-1 text-orange-600">
                      <Flame size={16} />
                      <span className="text-sm font-bold">{getCompletedDuration()} min done</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 overflow-x-auto">
                  <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
                    {routine.physical_exercise.exercises.map((exercise) => (
                      <button
                        key={exercise.id}
                        onClick={() => toggleExercise(exercise.id)}
                        className={`flex-shrink-0 w-40 p-4 rounded-xl border-2 transition-all text-left ${
                          exercise.completed
                            ? 'bg-orange-50 border-orange-300'
                            : 'bg-gray-50 border-gray-200 hover:border-orange-200'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mb-3 ${
                          exercise.completed
                            ? 'bg-orange-500 border-orange-500'
                            : 'border-gray-300'
                        }`}>
                          {exercise.completed && <Check size={16} className="text-white" strokeWidth={3} />}
                        </div>
                        <p className={`font-semibold text-sm mb-1 ${
                          exercise.completed ? 'text-orange-700' : 'text-gray-900'
                        }`}>
                          {exercise.text}
                        </p>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Clock size={12} />
                          <span className="text-xs">{exercise.duration} min</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="px-4 pb-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                    <p className="text-xs text-orange-700 font-medium">{routine.physical_exercise.notes}</p>
                  </div>
                </div>
              </div>
              )}

              {/* MENTAL EXERCISE - Timeline style */}
              {routine.mental_exercise.activities.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-purple-600" strokeWidth={2} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-bold text-gray-900">Mental Wellness</h2>
                      <p className="text-xs text-gray-500">{routine.mental_exercise.total_duration} min mindfulness</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-purple-600">
                        {routine.mental_exercise.activities.filter(a => a.completed).length}/{routine.mental_exercise.activities.length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                    
                    <div className="space-y-4">
                      {routine.mental_exercise.activities.map((activity, index) => (
                        <button
                          key={activity.id}
                          onClick={() => toggleMentalActivity(activity.id)}
                          className="w-full flex items-start gap-4 relative"
                        >
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center z-10 flex-shrink-0 transition-all ${
                            activity.completed
                              ? 'bg-purple-500 border-purple-500'
                              : 'bg-white border-gray-300'
                          }`}>
                            {activity.completed ? (
                              <Check size={14} className="text-white" strokeWidth={3} />
                            ) : (
                              <span className="text-xs font-bold text-gray-400">{index + 1}</span>
                            )}
                          </div>
                          <div className={`flex-1 p-3 rounded-xl text-left transition-all ${
                            activity.completed
                              ? 'bg-purple-50 border border-purple-200'
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}>
                            <p className={`font-semibold text-sm ${
                              activity.completed ? 'text-purple-700' : 'text-gray-900'
                            }`}>
                              {activity.text}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">{activity.duration} minutes</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="px-4 pb-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
                    <p className="text-xs text-purple-700 font-medium">{routine.mental_exercise.notes}</p>
                  </div>
                </div>
              </div>
              )}

              {/* CUSTOM HABITS */}
              {customHabits.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-pink-600" strokeWidth={2} />
                      </div>
                      <div className="flex-1">
                        <h2 className="font-bold text-gray-900">Custom Habits</h2>
                        <p className="text-xs text-gray-500">Your personal goals for today</p>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-pink-600">
                          {customHabits.filter(h => h.is_completed).length}/{customHabits.length}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 space-y-2">
                    {customHabits.map((habit) => (
                      <button
                        key={habit.id}
                        onClick={() => toggleCustomHabit(habit.id, habit.is_completed)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                          habit.is_completed
                            ? 'bg-pink-50 border border-pink-200'
                            : 'bg-gray-50 border border-transparent hover:border-gray-200'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          habit.is_completed
                            ? 'bg-pink-500 border-pink-500'
                            : 'border-gray-300'
                        }`}>
                          {habit.is_completed && <Check size={14} className="text-white" strokeWidth={3} />}
                        </div>
                        <span className={`text-sm text-left flex-1 ${
                          habit.is_completed ? 'text-pink-700 line-through' : 'text-gray-700'
                        }`}>
                          {habit.habit_name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Motivational Footer */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200 text-center">
                <p className="text-gray-600 text-sm font-bold uppercase tracking-wider mb-1">
                  Keep Going
                </p>
                <p className="text-cyan-600 text-2xl font-black mb-2">
                  {getTotalCompletion() >= 100 ? "🎉 All Done!" : 
                   getTotalCompletion() >= 75 ? "Almost There!" :
                   getTotalCompletion() >= 50 ? "Halfway Done!" :
                   "You've Got This!"}
                </p>
                <p className="text-gray-500 text-xs font-medium">
                  Every small step counts towards your goal 💪
                </p>
              </div>
            </>
          )}
        </div>

        {/* Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 safe-area-inset-bottom">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-around">
            <button
              onClick={() => onNavigate('home')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <HomeIcon size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Home</span>
            </button>
            <button
              onClick={() => onNavigate('progress')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <TrendingUp size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Progress</span>
            </button>
            <button
              onClick={() => onNavigate('activity')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <ActivityIcon size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Activity</span>
            </button>
            <button
              onClick={() => onNavigate('profile')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <User size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Profile</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
}